import React from "react";
import axios from "axios";
import { X, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { SERVICES } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ConsultationDialog({ open, onClose, defaultService = "" }) {
  const [form, setForm] = React.useState({ name: "", email: "", phone: "", service: defaultService, message: "" });
  const [status, setStatus] = React.useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setStatus("idle");
      setErrorMsg("");
      setForm((f) => ({ ...f, service: defaultService || f.service }));
    }
  }, [open, defaultService]);

  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    if (open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await axios.post(`${API}/consultations`, form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      setStatus("error");
      setErrorMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  return (
    <div data-testid="consultation-dialog" className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close overlay"
        onClick={onClose}
        className="absolute inset-0 bg-[#0A192F]/60 backdrop-blur-sm"
        data-testid="consultation-overlay"
      />
      <div className="relative w-full max-w-2xl bg-white border border-stone-200 shadow-2xl">
        <div className="flex items-start justify-between p-7 border-b border-stone-200">
          <div>
            <p className="overline text-[#C5A059]">Book a Consultation</p>
            <h3 className="font-serif text-3xl text-[#0A192F] mt-2">A confidential conversation, on your terms.</h3>
            <p className="text-[#475569] mt-2 text-sm max-w-md">Share a few details and we will reach out within one working day to schedule a call.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="consultation-close-button"
            className="p-2 text-[#0A192F] hover:bg-stone-100"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        {status === "success" ? (
          <div data-testid="consultation-success" className="p-10 text-center">
            <CheckCircle2 className="mx-auto text-[#C5A059]" size={42} />
            <h4 className="font-serif text-2xl text-[#0A192F] mt-5">Thank you — we have received your inquiry.</h4>
            <p className="text-[#475569] mt-3 max-w-md mx-auto">A member of our team will be in touch within one working day.</p>
            <button type="button" onClick={onClose} className="btn-ghost-dark mt-7">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-1">
              <label className="overline text-[10px] text-[#64748B]">Full Name</label>
              <input
                required minLength={2}
                value={form.name} onChange={onChange("name")}
                data-testid="form-name"
                className="mt-2 w-full border border-stone-300 px-4 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-white"
                placeholder="Your name"
              />
            </div>
            <div className="md:col-span-1">
              <label className="overline text-[10px] text-[#64748B]">Phone</label>
              <input
                required
                value={form.phone} onChange={onChange("phone")}
                data-testid="form-phone"
                className="mt-2 w-full border border-stone-300 px-4 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-white"
                placeholder="+91…"
              />
            </div>
            <div className="md:col-span-1">
              <label className="overline text-[10px] text-[#64748B]">Email</label>
              <input
                required type="email"
                value={form.email} onChange={onChange("email")}
                data-testid="form-email"
                className="mt-2 w-full border border-stone-300 px-4 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-white"
                placeholder="you@example.com"
              />
            </div>
            <div className="md:col-span-1">
              <label className="overline text-[10px] text-[#64748B]">Area of Interest</label>
              <select
                value={form.service} onChange={onChange("service")}
                data-testid="form-service"
                className="mt-2 w-full border border-stone-300 px-4 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-white"
              >
                <option value="">Select a service</option>
                {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="overline text-[10px] text-[#64748B]">How can we help?</label>
              <textarea
                required minLength={5} rows={4}
                value={form.message} onChange={onChange("message")}
                data-testid="form-message"
                className="mt-2 w-full border border-stone-300 px-4 py-3 text-[#0A192F] focus:border-[#C5A059] focus:outline-none bg-white resize-none"
                placeholder="Briefly describe your matter — keep it confidential."
              />
            </div>

            {status === "error" && (
              <div data-testid="consultation-error" className="md:col-span-2 flex items-center gap-2 text-[#B91C1C] text-sm">
                <AlertCircle size={16} /> {errorMsg}
              </div>
            )}

            <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2">
              <p className="text-xs text-[#64748B]">By submitting, you agree to be contacted regarding your inquiry.</p>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="form-submit"
                className="btn-primary"
              >
                {status === "loading" ? (<><Loader2 className="animate-spin" size={16}/> Sending…</>) : "Submit Inquiry"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
